// https://gbdev.io/pandocs/CPU_Instruction_Set.html
// https://www.pastraiser.com/cpu/gameboy/gameboy_opcodes.html
// http://marc.rawer.de/Gameboy/Docs/GBCPUman.pdf
// https://mgba-emu.github.io/gbdoc/#tech-specs
// https://imrannazar.com/GameBoy-Emulation-in-JavaScript:-The-CPU

import * as utils from "./utils"
import * as memory from './memory'
import * as cpu from './cpu'
import * as gpu from './gpu'
import * as screen from './screen'
import * as gameboy from './gameboy'
import './debug'
import opcodes from './opcodes.json'

type DisassembleLine = {
    addressNum: number,
    address: string,
    bytes: string,
    mnemonic: string,
    isOpSupported: boolean
}

const view = (() => {
    const values = {
        title: '',
        AF: utils.toHex(0, 2),
        BC: utils.toHex(0, 2),
        DE: utils.toHex(0, 2),
        HL: utils.toHex(0, 2),
        SP: utils.toHex(0, 2),
        PC: utils.toHex(0, 2),
        PCNum: 0,
        z: 0,
        n: 0,
        h: 0,
        c: 0,
        op: utils.toHex(0, 1),
        clock: 0,
        LY: utils.toHex(0, 1),
        IME: 0,
        IE: utils.toHex(0, 1),
        IF: utils.toHex(0, 1),
        LCDC: utils.toHex(0, 1),
        disassemble: [] as DisassembleLine[],
        step: () => step(),
        step1000: () => step1000(),
        animate: () => animate(),
        showTiles: () => screen.showTiles(),
        showScreen: () => screen.showScreen(),
    }

    // @ts-ignore
    return zam(document.body, values) as typeof values
})()

// @ts-ignore
window.view = view

async function main() {
    await loadROM()

    console.log(memory.get(0x990C))

    let title = ''
    for (let i = 0x0134; memory.get(i) != 0 && i < 0x0143; i++)
        title += String.fromCharCode(memory.get(i))

    console.log(title)
    view.title = title.toString()

    gameboy.reset()

    //for (let i = 0; i < 18488; i++)
    while (cpu.registers.PC != 0x29d5)// 0x2CD // 0x27D6 // 0x2C7 // 0x27A4
        if (!gameboy.step())
            break

    updateView()
}

async function loadROM() {
    const response = await fetch('/Tetris/Tetris.gb')
    const arrayBuffer = await response.arrayBuffer()
    const rom = new Uint8Array(arrayBuffer)

    gameboy.loadROM(rom)

    //disassemble(rom, [[0x000, 0x100], [0x150, 0x348], [0x2795, 0x2850], [0x29A6, 0x2A00], [0x6700, 0x6B00]])
    disassemble(rom, [[0x000, 0x400], [0x2795, 0x2850], [0x29A6, 0x2A00], [0x6700, 0x6B00]])
}

function step() {
    console.log(`OP ${utils.toHex(cpu.registers.op, 1)}`)

    gameboy.step()

    updateView()
}

function step1000() {
    for (let i = 0; i < 500000; i++)
        gameboy.step()

    updateView()
}

function animate() {
    let i = 0
    setInterval(() => {
        for (let i = 0; i < 10000; i++)
            gameboy.step()
        updateView()
    }, 1)
}

function updateView() {
    view.AF = utils.toHex(cpu.registers.AF, 2)
    view.BC = utils.toHex(cpu.registers.BC, 2)
    view.DE = utils.toHex(cpu.registers.DE, 2)
    view.HL = utils.toHex(cpu.registers.HL, 2)
    view.SP = utils.toHex(cpu.registers.SP, 2)
    view.PC = utils.toHex(cpu.registers.PC, 2)
    view.PCNum = cpu.registers.PC
    view.z = cpu.registers.z
    view.n = cpu.registers.n
    view.h = cpu.registers.h
    view.c = cpu.registers.c
    view.op = utils.toHex(cpu.registers.op, 1) + ' - ' + getOpcodeMnemonic(cpu.registers.op)
    view.clock = cpu.clock
    view.LY = utils.toHex(memory.get(0xFF44), 1)
    view.IME = cpu.registers.IME
    view.IE = utils.toHex(cpu.registers.IE, 1)
    view.IF = utils.toHex(cpu.registers.IF, 1)
    view.LCDC = utils.toHex(cpu.registers.LCDC, 1)

    view.disassemble = []
    if (cpu.registers.PC in addressToDisassembly) {
        let line = addressToDisassembly[cpu.registers.PC]
        for (let i = Math.max(line - 3, 0); i <= Math.min(line + 3, fullDisassembly.length); i++)
            view.disassemble.push(fullDisassembly[i])
    }
}

const fullDisassembly = [] as DisassembleLine[]
const addressToDisassembly = {} as { [addr: number]: number }

function disassemble(rom: Uint8Array, ranges: [from: number, to: number][]) {
    for (let range of ranges) {
        let addr = range[0]

        while (addr <= range[1]) {
            let op = rom[addr]

            let opCodeData = getOpcodeData(op)

            if (opCodeData != null) {
                let length = getOpcodeLength(op)

                let isCB = op == 0xCB

                if (isCB) {
                    op = rom[addr + 1]
                    length++
                }

                const bytes: string[] = []
                for (let i = 0; i < length; i++)
                    bytes.push(utils.toHexWithoutPrefix(rom[addr + i], 1))

                while (bytes.length < 3)
                    bytes.push('&nbsp;&nbsp;')


                const row = {
                    addressNum: addr,
                    address: utils.toHexWithoutPrefix(addr, 2),
                    bytes: bytes.join(' '),
                    mnemonic: isCB ? getOpcodeMnemonicCB(op) : getOpcodeMnemonic(op),
                    isOpSupported: isCB ? true : cpu.isOpSupported(op)
                }
                addressToDisassembly[addr] = fullDisassembly.length
                fullDisassembly.push(row)
                addr += length
            } else {
                const row = {
                    addressNum: addr,
                    address: utils.toHexWithoutPrefix(addr, 2),
                    bytes: utils.toHexWithoutPrefix(rom[addr], 1),
                    mnemonic: 'DB',
                    isOpSupported: true
                }
                addressToDisassembly[addr] = fullDisassembly.length
                fullDisassembly.push(row)
                addr++
            }
        }
    }
}

function getOpcodeMnemonic(op): string {
    const data = getOpcodeData(op)

    let result = data.mnemonic

    if ('operand1' in data)
        result += ' ' + data.operand1

    if ('operand2' in data)
        result += ',' + data.operand2

    return result
}

function getOpcodeMnemonicCB(op): string {
    const data = getOpcodeDataCB(op)

    let result = data.mnemonic

    if ('operand1' in data)
        result += ' ' + data.operand1

    if ('operand2' in data)
        result += ',' + data.operand2

    return result
}

function getOpcodeLength(op) {
    return getOpcodeData(op).length
}

function getOpcodeData(op) {
    const opHex = utils.toHex(op, 1).toLowerCase()
    return opcodes.unprefixed[opHex]
}

function getOpcodeDataCB(op) {
    const opHex = utils.toHex(op, 1).toLowerCase()
    return opcodes.cbprefixed[opHex]
}

main()